import { useState } from 'react'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import NumericButton from '~/bootcamp/Activities/Factorization/component/NumericButton'

import ArrowSelect, { ArrowDirection } from '~/bootcamp/Activities/Factorization/component/ArrowSelect'

import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFactorSelect from './useFactorSelect'

const DIRECTION_BY_INDEX = [ArrowDirection.LEFT, ArrowDirection.CENTER, ArrowDirection.RIGHT]

const FactorSelect = ({
  answerState,
  onAnswerStateChange,
  onNextQuestionClick,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
  difficultyLevel,
}: ActivityProps): React.ReactElement => {
  const [answer, setAnswer] = useState<number>(0)
  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const [questionPrime, options, verifyAnswer] = useFactorSelect(difficultyLevel, taskHistory)

  // Temporary until the levels are re-authored in Sanity; the correct option is never marked.
  useEmitInstance(
    onInstanceChange,
    questionPrime && options.length ? `${questionPrime}; options: ${options.join(', ')}` : null,
  )

  const onSelect = (selectedValue: number): void => {
    setAnswer(selectedValue)

    // Reset answer state
    onAnswerStateChange(null)
  }

  const checkAnswer = (): void => {
    const isCorrect: boolean = verifyAnswer(answer)
    onAnswerStateChange(isCorrect)
  }

  const nextQuestion = (): void => {
    setTaskHistory((prevTaskHistory) => [...prevTaskHistory, questionPrime].slice(-3))
    clearAnswer()
    onNextQuestionClick()
  }

  const clearAnswer = (): void => {
    setAnswer(0)

    // Reset answer state
    onAnswerStateChange(null)
  }

  return (
    <ActivityWrapper className="justify-between">
      <div className="flex-1 flex flex-col justify-center">
        <h2 className="text-center font-semibold text-4xl sm:text-6xl text-purple filter drop-shadow-md">
          {questionPrime}
        </h2>

        <div className="flex justify-between items-center space-x-3 w-full md:px-7">
          {options.map((primeNumber, index) => (
            <Option
              answerState={answerState}
              index={index}
              key={primeNumber}
              onClick={onSelect}
              selectedAnswer={answer}
              value={primeNumber}
            />
          ))}
        </div>
      </div>

      {SubmitButton && (
        <SubmitButton
          clearFormAction={clearAnswer}
          correctAnswer={!!answerState}
          formValid={answer !== 0}
          goToNextQuestionAction={nextQuestion}
          submitAnswerAction={checkAnswer}
        />
      )}
    </ActivityWrapper>
  )
}

const Option = ({
  value,
  onClick,
  answerState,
  index,
  selectedAnswer,
}: {
  value: number
  onClick: (value: number) => void
  answerState: boolean | null
  index: number
  selectedAnswer: number
}): React.ReactElement => {
  const isSelected = selectedAnswer === value
  const isWrong = selectedAnswer === value && answerState === false

  const handleClick = (): void => {
    onClick(value)
  }

  return (
    <div className="flex flex-col">
      <ArrowSelect
        className="my-5 sm:my-6"
        direction={DIRECTION_BY_INDEX[index]}
        isSelected={isSelected}
        isWrong={isWrong}
        onClick={handleClick}
      />
      <NumericButton isSelected={isSelected} isWrong={isWrong} onClick={handleClick}>
        {value}
      </NumericButton>
    </div>
  )
}

export default FactorSelect
